type IrisFeature = {
  feature_id: string;
  organization_id?: string;
  enabled: boolean;
  constant?: string;
  feature?: { constant: string };
};
type IrisProfile = {
  last_name?: string;
  photo?: string;
  phone?: { country?: string; number?: string };
  features?: IrisFeature[];
  organization_features?: IrisFeature[];
};

export type IrisSession = {
  account: {
    user_id: string;
    display_name: string;
    email: string;
    language: string;
    contexts: { product: string | number; organization_id: string; granted_roles: string[] }[];
  };
  session_id: string;
  organization_id: string;
  product: number;
  roles: string[];
  csrf: string;
  profile?: IrisProfile;
};

const runtime = window as unknown as { configuration?: Record<string, unknown> };
export const irisEnabled =
  runtime.configuration?.irisAuthMode !== undefined &&
  runtime.configuration?.irisAuthMode !== '' &&
  runtime.configuration?.irisAuthMode !== 'legacy';
let generation = 0;
let session: IrisSession | null = null;
let pending: Promise<IrisSession> | null = null;
export const currentSession = () => session;

export class IrisError extends Error {
  constructor(public status: number) {
    super(status === 401 ? 'Session ended' : 'Request could not be completed');
  }
}

export function clearLegacyCredentials() {
  ['accessToken', 'refreshToken', 'productionMonitoringSession', 'websocketToken', 'loadingWebsocketTokenLock'].forEach(
    key => localStorage.removeItem(key)
  );
}

export function sessionEnded() {
  generation += 1;
  pending = null;
  session = null;
  clearLegacyCredentials();
  window.dispatchEvent(new Event('iris:session-ended'));
}

export async function loadSession(force = false): Promise<IrisSession> {
  if (runtime.configuration?.irisAuthMode !== 'iris') throw new IrisError(503);
  if (session && !force) return session;
  if (pending) return pending;
  const started = generation;
  const request = fetch('/bff/session', { credentials: 'same-origin', cache: 'no-store', redirect: 'error' })
    .then(async response => {
      if (started !== generation) throw new IrisError(401);
      if (!response.ok) {
        if (response.status === 401) sessionEnded();
        throw new IrisError(response.status);
      }
      const value = (await response.json()) as IrisSession;
      if (!value.account?.user_id || !value.session_id || !value.csrf) throw new IrisError(503);
      if (started !== generation) throw new IrisError(401);
      if (
        session &&
        (session.account.user_id !== value.account.user_id ||
          session.organization_id !== value.organization_id ||
          session.session_id !== value.session_id)
      ) {
        window.dispatchEvent(new Event('iris:identity-changed'));
        throw new IrisError(409);
      }
      const profileResponse = await fetch(`/api/v1/users/${encodeURIComponent(value.account.user_id)}`, {
        credentials: 'same-origin',
        cache: 'no-store',
        redirect: 'error'
      });
      if (!profileResponse.ok) {
        if (profileResponse.status === 401) sessionEnded();
        throw new IrisError(profileResponse.status);
      }
      value.profile = (await profileResponse.json()) as IrisProfile;
      if (started !== generation) throw new IrisError(401);
      const previous = session;
      session = value;
      clearLegacyCredentials();
      if (
        previous &&
        JSON.stringify({ account: previous.account, roles: previous.roles, profile: previous.profile }) !==
          JSON.stringify({ account: value.account, roles: value.roles, profile: value.profile })
      )
        window.dispatchEvent(new CustomEvent('iris:session-updated', { detail: value }));
      return value;
    })
    .finally(() => {
      if (pending === request) pending = null;
    });
  pending = request;
  return pending;
}

export async function irisRequest<T = unknown>(
  path: string,
  method = 'GET',
  body?: unknown,
  authenticated = true,
  extraHeaders: Record<string, string> = {}
): Promise<T> {
  if (
    !path.startsWith('/') ||
    path.startsWith('//') ||
    path.includes('\\') ||
    new URL(path, window.location.origin).origin !== window.location.origin
  )
    throw new IrisError(400);
  const account = authenticated ? await loadSession() : null;
  const response = await fetch(path, {
    method,
    credentials: 'same-origin',
    cache: 'no-store',
    redirect: 'error',
    headers: {
      ...Object.fromEntries(
        Object.entries(extraHeaders).filter(([key]) => ['idempotency-key', 'if-match'].includes(key.toLowerCase()))
      ),
      'Content-Type': 'application/json',
      ...(account ? { 'X-CSRF-Token': account.csrf } : {})
    },
    body: body === undefined ? undefined : JSON.stringify(body)
  });
  if (!response.ok) {
    if (authenticated && response.status === 401) sessionEnded();
    throw new IrisError(response.status);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export async function logout(all = false) {
  await irisRequest(all ? '/auth/logout-all' : '/auth/logout', 'POST');
  sessionEnded();
  if ('BroadcastChannel' in window) {
    const channel = new BroadcastChannel('iris-session');
    channel.postMessage('recheck');
    channel.close();
  }
  window.location.assign('/login');
}

export function legacyUser(value: IrisSession, profile: IrisProfile = value.profile || {}) {
  let role = 'urn:roles:viewer';
  if (value.roles.some(item => item.includes('operator'))) role = 'urn:roles:operator';
  if (value.roles.includes('customer-admin') || value.roles.includes('manager-admin')) role = 'urn:roles:manager';
  return {
    ...profile,
    features: (
      profile.organization_features ||
      profile.features ||
      value.profile?.organization_features ||
      value.profile?.features ||
      []
    )
      .filter(feature => !feature.organization_id || feature.organization_id === value.organization_id)
      .map(feature => ({
        feature_id: feature.feature_id,
        enabled: feature.enabled,
        constant: feature.constant || feature.feature?.constant
      })),
    id: value.account.user_id,
    'e-mail': value.account.email,
    first_name: value.account.display_name,
    language: value.account.language || 'en_US',
    is_active: true,
    organizations: { [value.organization_id]: role },
    organization: { id: value.organization_id, userRole: role },
    roles_notify: [],
    exp: 0
  };
}

if (irisEnabled) {
  clearLegacyCredentials();
  if ('BroadcastChannel' in window) {
    const channel = new BroadcastChannel('iris-session');
    channel.onmessage = () => {
      loadSession(true).catch(() => undefined);
    };
  }
  window.addEventListener('focus', () => {
    if (session) loadSession(true).catch(() => undefined);
  });
}
